import { useEffect, useMemo, useRef, type Dispatch } from 'react'
import { createBrowserStepHistory, type StepHistory } from '../model/historyAdapter'
import { reduce, stepOf, type BookingStep, type Event, type Machine } from '../model/machine'

/** Order of the booking steps: a move to a higher rank pushes an entry, a move back goes back in history. */
const RANK: Record<BookingStep, number> = { 'P-07': 0, 'P-08': 1, 'P-09': 2, 'P-10': 3, 'P-11': 3, 'P-12': 3 }
/** P-09 → P-10 and P-10 → P-11 / P-12 replace the current entry (Frontend §4.7). */
const REPLACES: ReadonlySet<BookingStep> = new Set(['P-10', 'P-11', 'P-12'])

/**
 * Keeps the browser history in step with the machine on the direct link (Frontend §4.7, UserFlows §2.4 "Navigation
 * and refresh"): P-07, P-08 and P-09 push entries on the same URL; P-10, P-11 and P-12 replace; in-app Back moves
 * back through history. Browser Back / Forward dispatch `POP`; the reducer decides where that lands (P-12 stays, P-09
 * needs a live hold), the entry is corrected when it lands elsewhere, and leaving P-09 releases the hold (#9).
 * Entry screens have no entries. A new boot (P-16 "Start again") starts a new history.
 */
export function useBookingHistory(
  m: Machine,
  dispatch: Dispatch<Event>,
  releaseHold: (holdId: string) => void,
  make: (sessionId: string) => StepHistory = createBrowserStepHistory,
): void {
  const bootId = m.bootId
  const history = useMemo(() => make(`${String(bootId)}-${crypto.randomUUID()}`), [make, bootId])
  const machine = useRef(m)
  const shown = useRef<BookingStep | undefined>(undefined)
  const step = stepOf(m.state)

  useEffect(() => {
    machine.current = m
  })

  // A new history: nothing shown on it yet.
  useEffect(() => {
    shown.current = undefined
  }, [history])

  useEffect(() => {
    // Entry screens (and the options refetch) keep whatever entry is current.
    if (step === undefined) return
    const before = shown.current
    if (step === before) return
    shown.current = step
    if (before === undefined) history.start(step)
    else if (REPLACES.has(step)) history.replace(step)
    // P-10 → P-09 with P-13 (task 16; Frontend §3.5 "back to P-09 (replace)"): P-10 replaced P-09's entry.
    else if (before === 'P-10' && step === 'P-09') history.replace(step)
    else if (RANK[step] > RANK[before]) history.push(step)
    else history.back(step)
  }, [history, step])

  useEffect(
    () =>
      history.onPop((target) => {
        const current = machine.current
        const next = reduce(current, { type: 'POP', step: target })
        const landed = stepOf(next.state)
        if (current.state.name === 'review' && next.state.name !== 'review') releaseHold(current.state.hold.id)
        if (landed !== undefined && landed !== target) history.replace(landed)
        shown.current = landed ?? shown.current
        dispatch({ type: 'POP', step: target })
      }),
    [dispatch, history, releaseHold],
  )
}
